import { useDrawerEscape } from '../../../lib/hooks/useDrawerEscape';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../../AppProvider';
import { ActivityTab } from './ActivityTab';
import { NoteTab } from './NoteTab';
import { DrawerHeader, DrawerFooter } from './DrawerChrome';
import { useActivitySave } from './useActivitySave';
import { useActivityTypeOptions } from './useActivityTypeOptions';
import { useDrawerForm } from './useDrawerForm';
import { composeActivityDates, hasNoteContent } from './drawerShellHelpers';
import { resolveActivityTitle } from '../timeline/activityTitleFromDescription';
import type { DrawerUserOption } from './useDrawerData';
import { CalendarSection, type OrganizerCandidate } from './CalendarSection';
import type { ActivityNoteDrawerProps } from './types';
import { useFirstOpenTour } from '../../../lib/onboarding/useFirstOpenTour';
import { bookingError } from '../../../lib/calendar/booking';

const DRAWER_REDESIGN_TOUR_ID = 'drawer-redesign';

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';

/** Fonte :2059-2073 — assegnatario + commerciale, quest'ultimo solo se diverso. */
function buildOrganizerCandidates(users: DrawerUserOption[], agentId: string | null, commercialId: string | null): OrganizerCandidate[] {
  // `calendarId` qui è solo il flag "ha Google Calendar" consumato da CalendarSection
  // (convenzione '' = pulsante disabilitato): il calendario REALE dell'organizzatore è
  // risolto al submit da `resolveGoogleOrganizer` (activityPayload.ts) sul profilo utente.
  const hasGoogle = (id: string) => !!users.find((u) => u.id === id)?.google_workspace_scopes?.includes(GOOGLE_CALENDAR_SCOPE);
  const candidates: OrganizerCandidate[] = [];
  // Label UI: "Agente" è BANDITA dal glossario ruoli del redesign — è la stessa persona di
  // "Assegnata a". Il valore interno passato a `saveActivity` resta 'agent'/'commercial'.
  if (agentId) candidates.push({ userId: agentId, label: 'Assegnatario', calendarId: hasGoogle(agentId) ? 'primary' : '' });
  if (commercialId && commercialId !== agentId) {
    candidates.push({ userId: commercialId, label: 'Commerciale', calendarId: hasGoogle(commercialId) ? 'primary' : '' });
  }
  return candidates;
}

/**
 * Shell del drawer condiviso Attività/Nota: SOLO orchestrazione (tab, slot `statusSection`,
 * ordine di submit, guardie di chiusura). Nessuna query qui: le letture stanno in
 * `useDrawerData`, lo stato del form e l'unica istanza TipTap in `useDrawerForm`, le
 * scritture nell'`EntityAdapter` e in `useActivitySave`.
 *
 * Ordine di submit (contratto): `validateEntityChanges()` → `commitEntityChanges()` →
 * [tab nota] `saveNote()` → [tab attività] `saveActivity()` → callback granulari.
 * Con `requireNote` il commit dell'entità NON viene chiamato (lo fa il consumer dentro
 * `await onNotesUpdated()`) e la chiusura è delegata al parent.
 *
 * La guardia "senza nota non si chiude" vale SOLO per la X e per il submit: Escape e
 * "Annulla" chiudono sempre, come nella fonte, perché sono la via di annullamento del
 * drag&drop Kanban (il parent riporta indietro la card in `onClose`).
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:950-1040 e :1687-1750 (submit), :1763-1770 (guardia
 * sulla X), :917/:2521 (Escape e Annulla senza guardia), :1786-1855 (layout/header),
 * :2483-2512 (toggle descrizione nell'evento).
 */
export function ActivityNoteDrawer({
  isOpen,
  onClose,
  adapter,
  defaultTab = 'note',
  requireNote = false,
  editingActivity,
  initialActivityData,
  initialNoteText,
  statusSection,
  translations: _translations,
  onNotesUpdated,
  onActivityAdded,
  onActivityUpdated,
  onSaved,
}: ActivityNoteDrawerProps) {
  const { toast } = useApp();
  const { saveActivity } = useActivitySave(adapter);
  const typeOptions = useActivityTypeOptions();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useDrawerForm({ isOpen, adapter, editingActivity, initialActivityData, initialNoteText, defaultTab });
  // `booking`: regola "Fissare gli appuntamenti" (Impostazioni › Calendario), null = nessuna
  const { session, data, editor, tab, note, activityData, booking } = form;
  // In modifica la persona gia' salvata resta valida anche se fuori dalla regola
  const savedAssigneeId = session?.editingActivity?.assigned_to ?? null;
  const assigneeError = tab === 'activity' && !(savedAssigneeId && activityData.assigned_to === savedAssigneeId)
    ? bookingError(booking, activityData.assigned_to)
    : null;
  const [showAssigneeError, setShowAssigneeError] = useState(false);
  useEffect(() => { if (!isOpen) setShowAssigneeError(false); }, [isOpen]);

  const salesRepId = adapter.salesRep?.current ?? null;
  const organizerCandidates = useMemo(
    () => buildOrganizerCandidates(data.users, activityData.assigned_to, salesRepId),
    [data.users, activityData.assigned_to, salesRepId]
  );

  // === Chiusura ===
  // La guardia "senza nota non si chiude" vale SOLO per la X e per il submit (fonte
  // :1763-1770, :962-967). Escape (:917) e "Annulla" (:2521) chiamano `onClose()` DIRETTO
  // anche nella fonte: nel drag&drop Kanban sono la via di ANNULLAMENTO (il parent, in
  // `handleDrawerClose`, rimuove l'override e riporta la card allo stadio di partenza) —
  // metterci la guardia intrappolerebbe l'utente nel drawer.
  const requestCloseGuarded = () => {
    if (requireNote && !hasNoteContent(note)) {
      toast.warning('Devi aggiungere una nota per completare il cambio di stato');
      return;
    }
    onClose();
  };

  useDrawerEscape(isOpen,onClose,101);

  // Blocco dello scroll del body (fonte :913-934). Escape lo gestisce solo useDrawerEscape qui
  // sopra: un secondo listener chiudeva il pannello anche quando Escape serviva a chiudere il
  // calendarietto della data o una tendina, e si perdevano le modifiche.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Tour di novità del redesign (logica in useFirstOpenTour, incl. fix round 1: switch
  // temporaneo su Attività quando si parte da Nota, altrimenti drawer-props/drawer-calendar
  // restano invisibili). `session` va passato per reference, MAI ricostruito qui.
  useFirstOpenTour({ tourId: DRAWER_REDESIGN_TOUR_ID, isOpen, requireNote, session, setTab: form.setTab });

  /** Titolo auto-popolato col tipo quando non obbligatorio e lasciato vuoto (fonte :982-992).
   * Titolo vuoto ma descrizione scritta: si usa la prima riga della descrizione. */
  const resolveTitle = () => {
    const typed = resolveActivityTitle(activityData.title, note);
    if (typed) return typed;
    const extra = data.alfaDocsTypeGroup?.options.find((o) => o.value === activityData.type)?.label;
    return typeOptions.find((o) => o.value === activityData.type)?.label || extra || activityData.type;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Nota obbligatoria (fonte :962-967): submit bloccato finché manca.
    if (requireNote && !hasNoteContent(note)) {
      toast.error('Devi aggiungere una nota per completare il cambio di stato');
      return;
    }
    if (tab === 'activity' && data.activityTitleRequired && !resolveActivityTitle(activityData.title, note)) {
      // Né titolo né descrizione: messaggio esplicito e cursore sul titolo (prima il
      // pulsante sembrava non fare nulla se l'errore restava fuori vista).
      form.setTitleError(true);
      toast.error("Scrivi un titolo (o una descrizione) per salvare l'attività");
      const titleInput = document.getElementById('activity-title') as HTMLInputElement | null;
      titleInput?.scrollIntoView({ block: 'center' });
      titleInput?.focus();
      return;
    }
    form.setTitleError(false);

    // Regola "Fissare gli appuntamenti": persona obbligatoria e fra quelle ammesse
    if (assigneeError) {
      setShowAssigneeError(true);
      toast.error(assigneeError);
      return;
    }

    // Org non ancora risolta: si blocca PRIMA di qualunque scrittura (se il check stesse più
    // in basso, l'entità risulterebbe già committata e il parent mai notificato).
    if (tab === 'activity' && !data.organizationId) {
      toast.error('Organizzazione non disponibile: riprova');
      return;
    }

    // 1) Validazione delle modifiche all'entità (fase/assegnatario/… dello `statusSection`).
    const validationError = adapter.validateEntityChanges();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      // 2) Commit dell'entità — MAI nel flusso `requireNote`: lì è il consumer a farlo
      //    dentro `await onNotesUpdated()` (fonte :999-1001, :1712).
      if (!requireNote) await adapter.commitEntityChanges();

      // 3) Nota (tab nota).
      if (tab === 'note' && hasNoteContent(note)) {
        await adapter.saveNote(note, form.files);
      }

      // 4) Attività (tab attività).
      if (tab === 'activity') {
        const { startDateTime, endDateTime } = composeActivityDates(activityData.startDateTime, activityData.endDateTime);
        const saved = await saveActivity({
          activityData: {
            title: resolveTitle(),
            type: activityData.type,
            status: activityData.status,
            startDateTime,
            endDateTime,
            assigned_to: activityData.assigned_to,
            description: note,
            outcome: activityData.outcome,
          },
          editingActivity: session?.editingActivity ?? null,
          requireAssignee: !!booking,
          syncOptIn: form.syncOptIn,
          meetingProvider: form.meetingProvider,
          selectedAttendees: form.selectedAttendees,
          invitedCommercial: salesRepId,
          meetOrganizer: form.organizerId && salesRepId && form.organizerId === salesRepId ? 'commercial' : 'agent',
          includeDescriptionInEvent: form.includeDescriptionInEvent,
          includeDescriptionKnown: form.includeDescriptionKnown,
          // già verificata sopra fra le validazioni iniziali (submit bloccato se assente)
          organizationId: data.organizationId as string,
          toast,
          clientName: data.client.name,
          clientPhone: data.client.phone,
          clientEmail: data.client.email,
        });
        // saveActivity ritorna null se l'insert/update fallisce (useActivitySave ha già
        // mostrato il toast d'errore, senza throw): si esce SENZA chiudere il drawer, SENZA
        // resettare nota/editor/allegati e SENZA chiamare onNotesUpdated/callback di successo,
        // così l'utente ritrova il contenuto digitato intatto (come già per la nota fallita).
        // L'eventuale commit dell'entità già avvenuto al passo 2 resta com'è; il retry è
        // protetto dai ref anti-riesecuzione degli adapter.
        if (!saved) return;
        if (session?.editingActivity) onActivityUpdated?.();
        else onActivityAdded?.();
        onSaved?.(saved);
      }

      // 5) Callback granulari: `onNotesUpdated` è AWAITATA — nel flusso `requireNote` è lì
      //    che il consumer committa lo stato dell'entità e poi chiude il drawer.
      await onNotesUpdated();

      // Chiusura DELEGATA al parent quando la nota è obbligatoria (fonte :1738-1742).
      // Il reset di nota/editor/allegati avviene SOLO sul ramo che chiude davvero: se il
      // parent non chiude (errore nel suo commit di stato) l'utente deve ritrovare il
      // contenuto intatto, non un drawer vuoto con la nota persa. Sul ramo requireNote il
      // reset è comunque garantito alla riapertura (effect di init su rising edge).
      if (!requireNote) {
        form.setNote('');
        editor?.commands.setContent('');
        form.setFiles([]);
        onClose();
      }
    } catch (error) {
      console.error('[ActivityNoteDrawer] submit failed:', error);
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  /** Bottone primario contestuale (Task 2 del redesign): requireNote vince su tutto, poi
   * submitting, poi tab/editing. */
  const primaryLabel = isSubmitting
    ? 'Salvataggio...'
    : requireNote
      ? 'Salva e completa'
      : tab === 'note'
        ? 'Salva nota'
        : session?.editingActivity
          ? 'Aggiorna attività'
          : 'Salva attività';

  // Montato su document.body: dentro un contenitore con transform/animazione (colonna sinistra
  // della scheda Lead) il "fixed" si agganciava al contenitore e il riquadro usciva largo ~120px
  // in mezzo alla scheda invece che a destra dello schermo (segnalato da Luca 29/09).
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-flu-workspace="activity-backdrop"
            className="fixed inset-0 md:top-12 bg-black bg-opacity-50 z-[100]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            data-flu-workspace="activity-panel"
            className="fixed top-0 md:top-12 bottom-0 right-0 w-full sm:w-2/3 lg:w-1/2 xl:w-1/3 bg-white dark:bg-[#2F3437] shadow-xl z-[101] flex flex-col border-l border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]"
          >
            <div className="flex flex-col h-full">
              <DrawerHeader
                tab={tab}
                onTabChange={form.setTab}
                requireNote={requireNote}
                hideTabs={!!session?.editingActivity}
                closeDisabled={requireNote && !hasNoteContent(note)}
                isEditingActivity={!!session?.editingActivity}
                onRequestClose={requestCloseGuarded}
              />

              <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Slot dell'entità (fase/assegnatario/commerciale…): SOPRA le tab, visibile in entrambe */}
                  {statusSection}

                  {tab === 'activity' && (
                    <ActivityTab
                      activityData={activityData}
                      onActivityDataChange={form.setActivityData}
                      titleError={form.titleError}
                      onTitleErrorClear={() => form.setTitleError(false)}
                      activityTitleRequired={data.activityTitleRequired}
                      editingActivityId={session?.editingActivity?.id ?? null}
                      editingActivityGoogleEventId={session?.editingActivity?.google_event_id ?? null}
                      extraTypeGroups={data.alfaDocsTypeGroup ? [data.alfaDocsTypeGroup] : []}
                      aiPrefilled={!!initialActivityData}
                      users={data.users}
                      assigneeSyncSettings={data.settings}
                      currentUserId={data.currentUserId}
                      booking={booking}
                      keepAssigneeId={savedAssigneeId}
                      assigneeError={showAssigneeError || (booking?.start === 'none' && !activityData.assigned_to) ? assigneeError : null}
                    />
                  )}

                  {/* Editor UNICO, montato fuori dal tab switch (mai smontato al cambio tab):
                      cambiano solo la label e la visibilità degli allegati, come nella fonte. */}
                  <NoteTab
                    editor={editor}
                    files={form.files}
                    onFilesChange={form.setFiles}
                    requireNote={requireNote}
                    label={tab === 'note' ? 'Nota' : 'Descrizione'}
                    showAttachments={tab === 'note'}
                    disabled={isSubmitting}
                    toast={toast}
                  />

                  {/* Sezione Calendario IN CODA (ordine del redesign: titolo → proprietà →
                      Descrizione → Calendario). Prima viveva dentro ActivityTab, quindi
                      SOPRA l'editor montato qui dalla shell. */}
                  {tab === 'activity' && (
                    <CalendarSection
                      activityType={activityData.type}
                      syncOptIn={form.syncOptIn}
                      onSyncOptInChange={form.setSyncOptIn}
                      settings={data.settings}
                      meetingProvider={form.meetingProvider}
                      onMeetingProviderChange={form.setMeetingProvider}
                      attendees={form.selectedAttendees}
                      onAttendeesChange={form.setSelectedAttendees}
                      organizerCandidates={organizerCandidates}
                      organizerId={form.organizerId}
                      onOrganizerChange={form.setOrganizerId}
                      includeDescriptionInEvent={form.includeDescriptionInEvent}
                      onIncludeDescriptionChange={form.setIncludeDescriptionInEvent}
                      existingGoogleEventId={session?.editingActivity?.google_event_id ?? null}
                      teamsServiceEnabled={data.teamsServiceEnabled}
                      users={data.users}
                      availableContacts={data.availableContacts}
                      creatingMeet={isSubmitting && form.meetingProvider === 'google_meet'}
                    />
                  )}
                </form>
              </div>

              {/* "Annulla" chiude SEMPRE (fonte :2521): è la via di annullamento del
                  drag&drop Kanban — la guardia resta sulla X e sul submit. */}
              <DrawerFooter isSubmitting={isSubmitting} primaryLabel={primaryLabel} onCancel={onClose} onSubmit={handleSubmit} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default ActivityNoteDrawer;
